import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Activity,
  Flame,
  Gauge,
  Rabbit,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Turtle,
} from 'lucide-react';
import type { PipeOption } from '../../../ndt/components/FilterPanel';
import type { WeldAnalyticsBundle } from '../../types/weld';
import type { OverviewWeldRow } from '../WeldOverviewTable';
import { computeDashboardMetrics } from '../../utils/weldDashboardMetrics';
import { glossaryText } from '../../utils/weldFieldGlossary';
import type { PipeJobRef } from '../../utils/jobNumbersFromSetup';
import { useWeldDrillDownPrefetch } from '../../utils/useWeldDrillDownPrefetch';
// import { formatNdtPrefetchProgress } from '../../utils/formatNdtPrefetchProgress'; // Overview prefetch banner (commented out)
import { isWeldIqAnalyticsEmpty, isWeldIqUnavailable } from '../../utils/weldIqEmptyState';
import { WeldIqBundleStatusBanners } from '../WeldIqBundleStatusBanners';
import { ColumnHeaderHelp } from '../shared/ColumnHeaderHelp';
import { DashboardMetricCard } from './DashboardMetricCard';
import { PassFailDonutChart } from './PassFailDonutChart';
import { PassFailTimeline } from './PassFailTimeline';
import { DashboardSkeleton } from './DashboardSkeleton';
import type { DurationDrillDownFilter, WpsDrillDownVariant } from './drilldown/drillDownTypes';
import { DurationDrillDownHost } from './drilldown/DurationDrillDownHost';
import { WpsOutcomeDrillDownHost } from './drilldown/WpsOutcomeDrillDownHost';
import type { TimelinePoint } from '../../utils/weldDashboardMetrics';
import type { PendingWeldDrillDown } from './drilldown/drillDownTypes';

interface WeldVisualDashboardProps {
  bundle: WeldAnalyticsBundle | null;
  productionWeldData: OverviewWeldRow[];
  productionWeldDataLoading: boolean;
  productionStatusCalculationMethod: string;
  appliedJobNumbers: string[];
  appliedJobRefs?: PipeJobRef[];
  appliedPipeIds?: string[];
  isLoading: boolean;
  isRefreshing?: boolean;
  hasAppliedData?: boolean;
  error: string | null;
  pipeSelected: boolean;
  projectId: number | null;
  pipeId: string | null;
  pipeIds?: string[];
  pipeOptions?: PipeOption[];
  canEditNdt?: boolean;
  pendingDrillDown?: PendingWeldDrillDown | null;
  onPendingDrillDownConsumed?: () => void;
  onDrillDownClose?: () => void;
}

function formatSec(sec: number | null): string {
  if (sec == null) return '—';
  return sec < 60 ? `${sec.toFixed(1)}s` : `${(sec / 60).toFixed(2)} min`;
}

function wpsOutcomeLabel(outcome: TimelinePoint['outcome']): string {
  if (outcome === 'pass') return 'Within limit';
  if (outcome === 'fail') return 'Out of limit';
  return 'Other';
}

export const WeldVisualDashboard = ({
  bundle,
  productionWeldData,
  productionWeldDataLoading,
  productionStatusCalculationMethod,
  appliedJobNumbers,
  appliedJobRefs = [],
  appliedPipeIds = [],
  isLoading,
  isRefreshing = false,
  hasAppliedData = false,
  error,
  pipeSelected,
  projectId,
  pipeId,
  pipeIds: _pipeIds = [],
  pipeOptions = [],
  canEditNdt = false,
  pendingDrillDown = null,
  onPendingDrillDownConsumed,
  onDrillDownClose,
}: WeldVisualDashboardProps) => {
  const [drillDownVariant, setDrillDownVariant] = useState<WpsDrillDownVariant | null>(null);
  const [durationDrillDown, setDurationDrillDown] = useState<DurationDrillDownFilter | null>(null);
  const [preselectWeld, setPreselectWeld] = useState<{
    weldNumber: string;
    jobNumber: string;
  } | null>(null);
  /** Stable filter for single-weld scope; not cleared after preselect succeeds. */
  const [weldIdentityFilter, setWeldIdentityFilter] = useState<{
    weldNumber: string;
    jobNumber: string;
  } | null>(null);
  const [preselectNotice, setPreselectNotice] = useState<string | null>(null);
  const [singleWeldOnly, setSingleWeldOnly] = useState(false);
  const [jobOnlyFilter, setJobOnlyFilter] = useState<string | null>(null);

  const openSingleWeldDrillDown = useCallback(
    (weldNumber: string, jobNumber: string, label: string) => {
      setDurationDrillDown({
        type: 'weld',
        label,
        weldNumber,
        jobNumber,
      });
    },
    [],
  );

  useEffect(() => {
    if (!pendingDrillDown) return;
    setDurationDrillDown(null);
    setPreselectNotice(null);
    if (pendingDrillDown.scope === 'job') {
      setPreselectWeld(null);
      setWeldIdentityFilter(null);
      setSingleWeldOnly(false);
      setJobOnlyFilter(pendingDrillDown.jobNumber);
    } else {
      setJobOnlyFilter(null);
      const identity = {
        weldNumber: pendingDrillDown.weldNumber ?? '',
        jobNumber: pendingDrillDown.jobNumber,
      };
      setPreselectWeld(identity);
      const isSingle = pendingDrillDown.scope === 'single';
      setSingleWeldOnly(isSingle);
      setWeldIdentityFilter(isSingle ? identity : null);
    }
    setDrillDownVariant(pendingDrillDown.variant ?? 'compliance');
    onPendingDrillDownConsumed?.();
  }, [pendingDrillDown, onPendingDrillDownConsumed]);

  const metrics = useMemo(
    () =>
      computeDashboardMetrics(
        productionWeldData,
        bundle,
        productionStatusCalculationMethod,
        appliedJobNumbers,
        appliedJobRefs,
      ),
    [productionWeldData, bundle, productionStatusCalculationMethod, appliedJobNumbers, appliedJobRefs],
  );

  const prefetchEnabled =
    Boolean(hasAppliedData) &&
    pipeSelected &&
    appliedJobNumbers.length > 0 &&
    !isLoading &&
    !productionWeldDataLoading &&
    Boolean(bundle) &&
    productionWeldData.length > 0 &&
    metrics.welds.length > 0;

  const prefetch = useWeldDrillDownPrefetch({
    enabled: prefetchEnabled,
    projectId,
    pipeId,
    appliedPipeIds,
    appliedJobRefs,
    metrics,
    productionWeldData,
    bundle,
  });

  if (!pipeSelected) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 py-16">
        <p className="text-blueprint-blue">Select a pipe size to load Weld Analytics.</p>
      </div>
    );
  }

  if (
    (isLoading && !bundle) ||
    (productionWeldDataLoading && productionWeldData.length === 0 && !hasAppliedData)
  ) {
    return <DashboardSkeleton />;
  }

  if (appliedJobNumbers.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 py-16">
        <p className="text-center text-blueprint-blue">
          Select a pipe size (jobs load automatically). Open Filters to refine Job # if needed.
        </p>
      </div>
    );
  }

  // Only block Overview when production itself failed and there are no rows to show.
  if (error && productionWeldData.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-[#E2E5EA] bg-white py-16">
        <p className="text-lg text-readable-red">{error}</p>
      </div>
    );
  }

  if (metrics.welds.length === 0) {
    const showIqBanner =
      productionWeldData.length === 0 &&
      (isWeldIqUnavailable(bundle) || isWeldIqAnalyticsEmpty(bundle));
    return (
      <div className="space-y-4">
        {showIqBanner ? (
          <WeldIqBundleStatusBanners
            bundle={bundle}
            hasProductionData={productionWeldData.length > 0}
          />
        ) : null}
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 py-16">
          <Activity className="h-10 w-10 text-heritage-grey" />
          <p className="text-blueprint-blue">No weld data for the selected jobs.</p>
          <p className="text-sm text-heritage-grey">Try a different job number or adjust filters.</p>
        </div>
      </div>
    );
  }

  const { kpis } = metrics;
  const showIqBanner =
    productionWeldData.length === 0 &&
    (isWeldIqUnavailable(bundle) || isWeldIqAnalyticsEmpty(bundle));

  return (
    <div className="space-y-5">
      {showIqBanner ? (
        <WeldIqBundleStatusBanners
          bundle={bundle}
          hasProductionData={productionWeldData.length > 0}
        />
      ) : null}
      {/* Refresh banner — hidden on Overview for now.
      {isRefreshing && (
        <div className="flex items-center gap-2 rounded-lg border border-[#E2E5EA] bg-[rgba(0,39,63,0.03)] px-3 py-2 text-sm text-heritage-grey">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-hero-red border-t-transparent" />
          Updating weld analytics…
        </div>
      )}
      */}
      {/* Prefetch progress (NDT / pass summaries / live scoring) — hidden on Overview for now.
      {(prefetch.ndtBatchLoading ||
        prefetch.wpsBatchLoading ||
        prefetch.liveScoringBatchLoading) &&
        !isRefreshing && (
        <div className="flex items-start gap-2 rounded-lg border border-[#E2E5EA] bg-[rgba(0,39,63,0.03)] px-3 py-2 text-sm text-heritage-grey">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-heritage-grey" />
          <div className="min-w-0 space-y-0.5">
            {prefetch.ndtBatchLoading && (
              <p>
                {formatNdtPrefetchProgress({
                  phase: prefetch.ndtPhase,
                  phaseLoadedCount: prefetch.ndtPhaseLoadedCount,
                  phaseTargetCount: prefetch.ndtPhaseTargetCount,
                  loadedCount: prefetch.ndtLoadedCount,
                  totalCount: prefetch.allRows.length,
                })}
              </p>
            )}
            {prefetch.wpsBatchLoading && (
              <p>
                Pass summaries loaded for {Math.min(prefetch.wpsLoadedCount, prefetch.wpsTargetCount)} of{' '}
                {prefetch.wpsTargetCount} welds…
              </p>
            )}
            {prefetch.liveScoringBatchLoading && prefetch.liveScoringTargetCount > 0 && (
              <p>
                Live scoring ready for {prefetch.liveScoringLoadedCount} of{' '}
                {prefetch.liveScoringTargetCount} out-of-limit welds…
              </p>
            )}
          </div>
        </div>
      )}
      */}
      {preselectNotice && (
        <p className="rounded-lg border border-[#E2E5EA] bg-[rgba(230,161,0,0.08)] px-3 py-2 text-sm text-[#B38600]">
          {preselectNotice}
        </p>
      )}

      {/* <DashboardHeader
        jobNumbers={jobNumbers}
        dateLabel={metrics.headerDateLabel}
        totalComponents={metrics.welds.length}
        lastUpdatedMs={metrics.lastUpdatedMs}
      /> */}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardMetricCard
          label="Total Welds"
          value={String(kpis.totalWelds)}
          icon={Flame}
          accent="blue"
        />
        <DashboardMetricCard
          label={
            <ColumnHeaderHelp
              label="Welds within WPS limit"
              help={glossaryText('welds_within_wps_limit')}
            />
          }
          value={String(kpis.passedWelds)}
          icon={ThumbsUp}
          delta={kpis.passedDelta}
          accent="green"
          onClick={() => setDrillDownVariant('within')}
          // clickableHint="View welds"
        />
        <DashboardMetricCard
          label={
            <ColumnHeaderHelp
              label="Welds out of WPS limit"
              help={glossaryText('welds_out_of_wps_limit')}
            />
          }
          value={String(kpis.failedWelds)}
          icon={ThumbsDown}
          delta={kpis.failedDelta}
          invertDelta
          accent="red"
          onClick={() => setDrillDownVariant('out')}
          // clickableHint="View welds"
        />
        <DashboardMetricCard
          label={
            <ColumnHeaderHelp
              label="WPS limit compliance rate"
              help={glossaryText('wps_limit_compliance_rate')}
            />
          }
          value={kpis.passRatePct != null ? `${kpis.passRatePct.toFixed(1)}%` : '—'}
          icon={Gauge}
          progressPct={kpis.passRatePct}
          accent="green"
          onClick={() => setDrillDownVariant('compliance')}
          // clickableHint="View welds"
        />
        <DashboardMetricCard
          label={
            <ColumnHeaderHelp
              label="Avg Weld Duration"
              help={glossaryText('avg_weld_duration')}
            />
          }
          value={formatSec(kpis.avgDurationSec)}
          icon={Timer}
          delta={kpis.durationDelta}
          invertDelta
          accent="blue"
        />
        <DashboardMetricCard
          label="Fastest Weld"
          value={formatSec(kpis.fastestDurationSec)}
          icon={Rabbit}
          accent="green"
          onClick={() => {
            if (kpis.fastestDurationSec == null) return;
            setDurationDrillDown({
              type: 'exact',
              label: `Fastest welds (${formatSec(kpis.fastestDurationSec)})`,
              durationSec: kpis.fastestDurationSec,
            });
          }}
        />
        <DashboardMetricCard
          label="Slowest Weld"
          value={formatSec(kpis.slowestDurationSec)}
          icon={Turtle}
          accent="yellow"
          onClick={() => {
            if (kpis.slowestDurationSec == null) return;
            setDurationDrillDown({
              type: 'exact',
              label: `Slowest welds (${formatSec(kpis.slowestDurationSec)})`,
              durationSec: kpis.slowestDurationSec,
            });
          }}
        />
      </div>

      <PassFailDonutChart
        data={metrics.passFailDonut}
        passRatePct={kpis.passRatePct}
        onSegmentClick={(segment) => setDrillDownVariant(segment)}
      />

      <PassFailTimeline
        data={metrics.timeline}
        pipeOptions={pipeOptions}
        onStickClick={(point) => {
          openSingleWeldDrillDown(
            point.weldNumber,
            point.jobNumber,
            `Weld #${point.weldNumber} — ${wpsOutcomeLabel(point.outcome)}`,
          );
        }}
      />

      <WpsOutcomeDrillDownHost
        variant={drillDownVariant}
        onClose={() => {
          setDrillDownVariant(null);
          setPreselectWeld(null);
          setWeldIdentityFilter(null);
          setPreselectNotice(null);
          setSingleWeldOnly(false);
          setJobOnlyFilter(null);
          onDrillDownClose?.();
        }}
        metrics={metrics}
        productionWeldData={productionWeldData}
        bundle={bundle}
        statusCalculationMethod={productionStatusCalculationMethod}
        projectId={projectId}
        pipeId={pipeId}
        pipeOptions={pipeOptions}
        canEditNdt={canEditNdt}
        preselectWeld={preselectWeld}
        weldIdentityFilter={weldIdentityFilter}
        singleWeldOnly={singleWeldOnly}
        jobOnlyFilter={jobOnlyFilter}
        prefetch={prefetch}
        appliedPipeIds={appliedPipeIds}
        appliedJobRefs={appliedJobRefs}
        onPreselectResult={(result) => {
          if (result === 'missing') {
            setPreselectNotice(
              `Weld ${preselectWeld?.weldNumber ?? weldIdentityFilter?.weldNumber ?? ''} was not found in the WPS drill-down list for the current filters.`,
            );
          }
          setPreselectWeld(null);
        }}
      />

      <DurationDrillDownHost
        filter={durationDrillDown}
        onClose={() => setDurationDrillDown(null)}
        metrics={metrics}
        productionWeldData={productionWeldData}
        bundle={bundle}
        statusCalculationMethod={productionStatusCalculationMethod}
        projectId={projectId}
        pipeId={pipeId}
        pipeOptions={pipeOptions}
        canEditNdt={canEditNdt}
        prefetch={prefetch}
        appliedPipeIds={appliedPipeIds}
        appliedJobRefs={appliedJobRefs}
      />
    </div>
  );
};
